import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import mark from '../assets/hashquill-mark.svg';
import { REPO } from '../config';
import type { Viewer } from '../github/access';

export function Wordmark() {
  return (
    <a className="hq-wordmark" href="/" aria-label="Hashquill home">
      <img src={mark} alt="" />
      <span aria-hidden="true"><span className="hq-wordmark__hash">hash</span><span className="hq-wordmark__quill">quill</span></span>
    </a>
  );
}

const initials = (v: Viewer) =>
  (v.name?.trim() ? v.name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('') : v.login.slice(0, 2)).toUpperCase();

type MenuProps = { viewer: Viewer; onSignOut: () => void; onSwitchAccount: () => void; onChangeFolder?: (() => void) | undefined };

function AvatarMenu({ viewer, onSignOut, onSwitchAccount, onChangeFolder }: MenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="hq-avatar" ref={ref}>
      <button
        type="button" className="hq-avatar__button"
        aria-label={`Account menu for ${viewer.login}`} aria-haspopup="menu" aria-expanded={open} aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
      >
        {initials(viewer)}
      </button>
      {open && (
        <div className="hq-menu" role="menu" id={menuId}>
          <div className="hq-menu__label">Signed in as <strong>{viewer.login}</strong></div>
          {onChangeFolder && <button type="button" role="menuitem" className="hq-menu__item" onClick={() => { setOpen(false); onChangeFolder(); }}>Change folder</button>}
          <button type="button" role="menuitem" className="hq-menu__item" onClick={onSwitchAccount}>Switch GitHub account</button>
          <button type="button" role="menuitem" className="hq-menu__item" onClick={onSignOut}>Sign out</button>
        </div>
      )}
    </div>
  );
}

export function Header(props: Partial<MenuProps> & { nav?: ReactNode }) {
  const { viewer, onSignOut, onSwitchAccount, onChangeFolder, nav } = props;
  return (
    <header className="hq-header">
      <div className="hq-header__left">
        <Wordmark />
        <span className="hq-chip">{REPO.full}</span>
        {nav}
      </div>
      {viewer && onSignOut && onSwitchAccount && <AvatarMenu viewer={viewer} onSignOut={onSignOut} onSwitchAccount={onSwitchAccount} onChangeFolder={onChangeFolder} />}
    </header>
  );
}
